// Scoring engine for the Techmate Cyber Security & Compliance Check.
import {
  AREAS, CORE_QUESTIONS, INDUSTRY_MODULES, NOT_SURE_LABEL,
  resolveModuleKey, TIERS, READINESS_BANDS,
} from "../data/assessment";

const PROFILE_ORDER = {
  A2: ["1–20", "21–100", "101–500", "500+"],
  A3: ["Business records only", "Staff & supplier data", "Customer personal or payment data", "Regulated or highly sensitive data"],
  A4: ["No", "Unsure", "Yes — minor", "Yes — significant"],
};

function tierFor(score) {
  return TIERS.find((t) => score >= t.min) || TIERS[TIERS.length - 1];
}
function readinessFor(score) {
  return (READINESS_BANDS.find((b) => score >= b.min) || READINESS_BANDS[READINESS_BANDS.length - 1]).label;
}
function readinessColor(score) {
  return tierFor(score).color;
}

// Return every scored question that applies given the chosen sector module.
export function getScoredQuestions(profile) {
  const key = resolveModuleKey(profile);
  const industry = key === "other" ? [] : (INDUSTRY_MODULES[key] || []);
  return [...CORE_QUESTIONS, ...industry];
}

export function computeReport(profile, answers) {
  const scored = getScoredQuestions(profile);

  // Normalize an answer to an array of selected option labels.
  const asArray = (v) => (Array.isArray(v) ? v.filter(Boolean) : v ? [v] : []);

  // Step 1 — question scores + blind spots
  const perQuestion = {};
  const blindSpots = [];
  scored.forEach((q) => {
    const chosen = asArray(answers[q.id]);
    let score;
    let representative = chosen[0] || "";
    if (chosen.length === 0) {
      score = 0;
    } else if (chosen.length === 1 && chosen[0] === NOT_SURE_LABEL) {
      // Only "Not sure" selected → treat as uncertain
      score = 1;
      blindSpots.push({ control: q.control, area: AREAS[q.area].name });
    } else {
      // Ignore "Not sure" if other options are also selected; take the max maturity index.
      const filtered = chosen.filter((c) => c !== NOT_SURE_LABEL);
      const idxs = filtered.map((c) => q.options.indexOf(c)).filter((i) => i >= 0);
      score = idxs.length ? Math.max(...idxs) : 0;
      representative = filtered[filtered.length - 1] || representative;
    }
    perQuestion[q.id] = { score, chosen: representative, selections: chosen, q };
  });

  // Step 2 — area scores (0–100)
  const areaAgg = {};
  Object.keys(AREAS).forEach((k) => (areaAgg[k] = { num: 0, den: 0 }));
  scored.forEach((q) => {
    const s = perQuestion[q.id].score;
    areaAgg[q.area].num += s * q.weight;
    areaAgg[q.area].den += 4 * q.weight;
  });
  const areaScores = {};
  Object.keys(areaAgg).forEach((k) => {
    const { num, den } = areaAgg[k];
    areaScores[k] = den > 0 ? Math.round((num / den) * 100) : null;
  });

  // Step 3 — context weighting (multipliers compound)
  const w = {};
  Object.keys(AREAS).forEach((k) => (w[k] = 1.0));
  const a3i = PROFILE_ORDER.A3.indexOf(profile.A3);
  const a2i = PROFILE_ORDER.A2.indexOf(profile.A2);
  const a4i = PROFILE_ORDER.A4.indexOf(profile.A4);
  if (a3i >= 2) { w.protecting *= 1.25; w.personalData *= 1.25; w.leadership *= 1.15; w.knowing *= 1.15; }
  if (a3i === 3) { w.protecting *= 1.1; w.handling *= 1.1; }
  if (a2i >= 2) { w.leadership *= 1.15; w.knowing *= 1.1; }
  if (a4i >= 2) { w.handling *= 1.15; w.bouncing *= 1.1; w.spotting *= 1.1; }
  w.industry *= 1.3;

  // Step 4 — overall score (only areas with data)
  let onum = 0, oden = 0;
  Object.keys(AREAS).forEach((k) => {
    if (areaScores[k] != null) { onum += areaScores[k] * w[k]; oden += w[k]; }
  });
  const overall = oden > 0 ? Math.round(onum / oden) : 0;
  const tier = tierFor(overall);

  // Step 5 — risk register
  const risks = [];
  scored.forEach((q) => {
    const s = perQuestion[q.id].score;
    const gap = 4 - s;
    if (gap >= 2) {
      const severity = q.weight * gap;
      let level, color;
      if (severity >= 9) { level = "CRITICAL"; color = "var(--tcc-danger)"; }
      else if (severity >= 6) { level = "HIGH"; color = "#E2542F"; }
      else if (severity >= 4) { level = "MEDIUM"; color = "var(--tcc-warning)"; }
      else { level = "LOW"; color = "var(--tcc-blue)"; }
      risks.push({ id: q.id, severity, level, color, control: q.control, area: AREAS[q.area].name, risk: q.risk, action: q.action });
    }
  });
  risks.sort((a, b) => b.severity - a.severity);
  const criticalCount = risks.filter((r) => r.level === "CRITICAL").length;
  const highCount = risks.filter((r) => r.level === "HIGH").length;

  // Step 6 — areas built (for strongest/weakest + capability breakdown)
  const areaList = Object.keys(AREAS)
    .filter((k) => areaScores[k] != null)
    .map((k) => ({ key: k, ...AREAS[k], score: areaScores[k], tier: tierFor(areaScores[k]) }));
  const sortedByScore = [...areaList].sort((a, b) => b.score - a.score);
  const strongest = sortedByScore[0];
  const weakest = sortedByScore[sortedByScore.length - 1];

  // Peer comparison
  const peerAvg = Math.round(areaList.reduce((s, a) => s + a.peer, 0) / areaList.length);
  const diff = overall - peerAvg;
  let verdict;
  if (diff >= 8) verdict = "ahead of";
  else if (diff <= -8) verdict = "behind";
  else verdict = "in line with";

  // Step 7 — compliance scope
  const avg = (...keys) => {
    const vals = keys.map((k) => areaScores[k]).filter((v) => v != null);
    return vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
  };
  const scope = [];
  const dpdpR = avg("personalData", "protecting", "handling");
  scope.push({ name: "DPDP Act 2023", readiness: dpdpR, band: readinessFor(dpdpR), color: readinessColor(dpdpR),
    note: "Applies to every business in India that handles personal data — there is no size exemption. The Rules were notified in November 2025, with full compliance required by 13 May 2027. Penalties reach ₹250 crore for failure to implement reasonable security safeguards." });
  const certR = avg("spotting", "handling");
  scope.push({ name: "CERT-In Directions", readiness: certR, band: readinessFor(certR), color: readinessColor(certR),
    note: "Require cyber incidents to be reported within 6 hours, and security logs to be retained." });

  const key = resolveModuleKey(profile);
  if (key === "rbi") {
    const r = avg("industry", "leadership", "bouncing");
    scope.push({ name: "RBI cyber security framework", readiness: r, band: readinessFor(r), color: readinessColor(r), note: "Board-approved policy, VAPT, independent IS audit and incident reporting within hours." });
  } else if (key === "sebi") {
    const r = avg("industry", "leadership", "bouncing");
    scope.push({ name: "SEBI CSCRF", readiness: r, band: readinessFor(r), color: readinessColor(r), note: "Graded framework built on five resilience goals, with audit and reporting obligations by entity size." });
  } else if (key === "retail") {
    const r = avg("industry", "protecting");
    scope.push({ name: "PCI DSS", readiness: r, band: readinessFor(r), color: readinessColor(r), note: "Applies wherever card data is stored, processed or transmitted." });
  } else if (key === "healthcare") {
    // PCI applies if H2 <= index 2
    const h2 = perQuestion.H2 ? perQuestion.H2.score : 4;
    if (h2 <= 2) {
      const r = avg("industry", "protecting");
      scope.push({ name: "PCI DSS", readiness: r, band: readinessFor(r), color: readinessColor(r), note: "Applies where card payments are handled in-house." });
    }
  } else if (key === "it") {
    const r = avg("leadership", "knowing", "protecting");
    scope.push({ name: "ISO 27001 / SOC 2", readiness: r, band: readinessFor(r), color: readinessColor(r), note: "Customer-driven assurance standards, increasingly required to win enterprise contracts." });
  } else if (key === "professional") {
    const r = avg("leadership", "knowing", "protecting");
    scope.push({ name: "ISO 27001", readiness: r, band: readinessFor(r), color: readinessColor(r), note: "Frequently required by client contracts handling confidential data." });
  }

  // Verify-together lines (VERIFY question answered at score >= 3, not a blind spot)
  const verifyLines = [];
  scored.forEach((q) => {
    if (!q.verify) return;
    const pq = perQuestion[q.id];
    if (pq.selections.length === 1 && pq.selections[0] === NOT_SURE_LABEL) return;
    if (pq.score >= 3) {
      verifyLines.push({ chosen: pq.chosen, control: q.control });
    }
  });

  // Remediation roadmap buckets (de-duplicated actions)
  const dedupe = (arr) => {
    const seen = new Set();
    return arr.filter((r) => (seen.has(r.action) ? false : (seen.add(r.action), true)));
  };
  const immediate = dedupe(risks.filter((r) => r.level === "CRITICAL" || r.level === "HIGH")).slice(0, 4);
  const nearTerm = dedupe(risks.filter((r) => r.level === "MEDIUM")).slice(0, 4);
  const structural = dedupe(risks.filter((r) => r.level === "LOW")).slice(0, 4);

  return {
    profile, areaScores, areaList, w, overall, tier,
    risks, criticalCount, highCount,
    strongest, weakest,
    peerAvg, diff, verdict,
    scope, blindSpots, verifyLines,
    roadmap: { immediate, nearTerm, structural },
  };
}
